import CorrespondentModel from './correspondent-model';
import CurrencyModel from './currency-model';
import CustomerModel from './customer-model';
import DeliveryTimeModel from './delivery-time-model';
import DeliveryTypeModel from './delivery-type-model';
import ExchangeBankModel from './exchange-bank-model';
import ExchangeBankTypeModel from './exchange-bank-type-model';
import ExchangeStoreModel from './exchange-store-model';
import OperationNatureModel from './operation-nature-model';
import PaymentMethodModel from './payment-method-model';
import LiquidationModel from './liquidation-model';
import CustomerOperationalDocumentModel from './customer-operational-document-model';
import OperationDocumentModel from './operation-document-model';
import OperationCustomerAccountModel from './operation-customer-account-model';
import OperationPaymentAccountModel from './operation-payment-account-model';
import OperationAdministrativeModel from './operation-administrative-model';
import Workflow from '../../../common/models/Workflow';
import dateUtils from '../../../common/utils/date';
import numberUtils from '../../../common/utils/number';
import { DUIMP_STATUS, DUIMP_STATUS_LABELS } from '../../../common/constants/operation';

export default class OperationModel {
  constructor(obj) {
    obj = obj || {};

    const customerOperationalDocuments = obj.customerOperationalDocuments?.map(
      (d) => new CustomerOperationalDocumentModel(d)
    );
    const documents = obj.documents?.map((d) => new OperationDocumentModel(d));

    this.id = obj.id;
    this.exchangeType = new ExchangeBankTypeModel(obj.exchangeType);
    this.exchangeBuy = obj.exchangeBuy;
    this.customer = new CustomerModel(obj.customer);
    this.storeCity = obj.storeCity;
    this.bank = new ExchangeBankModel(obj.bank);
    this.store = new ExchangeStoreModel(obj.store);
    this.nature = new OperationNatureModel(obj.nature);
    this.deliveryType = new DeliveryTypeModel(obj.deliveryType);
    this.currency = new CurrencyModel(obj.currency);
    this.amount = obj.amount && parseFloat(obj.amount);
    this.currencyQuotation = obj.currencyQuotation;
    this.iof = obj.iof;
    this.iofValue = obj.iofValue;
    this.customerRateNoIOF = obj.customerRateNoIOF;
    this.customerRate = obj.customerRate;
    this.deliveryCost = obj.deliveryCost || 0;
    this.vet = obj.vet;
    this.totalValue = obj.totalValue;
    // Remessa tradicional: o valor foi digitado em reais (total) e a quantidade em M.E. é calculada.
    this.amountInBrl = !!obj.amountInBrl;
    this.locked = !!obj.locked;
    this.spread = obj.spread;
    this.spreadValue = obj.spreadValue;
    this.deliveryDate = obj.deliveryDate;
    this.travelDate = obj.travelDate;
    this.deliveryTime = new DeliveryTimeModel(obj.deliveryTime);
    this.observation = obj.observation;
    this.agent = new CorrespondentModel(obj.agent);
    this.paymentMethod = new PaymentMethodModel(obj.paymentMethod);
    this.cep = obj.cep;
    this.state = obj.state;
    this.city = obj.city;
    this.neighborhood = obj.neighborhood;
    this.street = obj.street;
    this.number = obj.number;
    this.complement = obj.complement;
    this.receiverName = obj.receiverName;
    this.receiverDocument = obj.receiverDocument;
    this.receiverCellphone = obj.receiverCellphone;
    this.liquidation = new LiquidationModel(obj.liquidation);
    this.liquidationBrlDays = obj.liquidationBrlDays ?? null;
    this.liquidationFxDays = obj.liquidationFxDays ?? null;
    this.account = new OperationCustomerAccountModel(obj.account);
    this.exchangeContractCost = obj.exchangeContractCost;
    this.exchangeContractCostBrl = obj.exchangeContractCostBrl;
    this.bankTariffBrl = obj.bankTariffBrl;
    this.useUsdExchangeContract = obj.useUsdExchangeContract;
    this.ir = obj.ir;
    this.irValue = obj.irValue;
    this.spreadMin = obj.spreadMin;
    this.spreadMax = obj.spreadMax;
    this.customerRateMin = obj.customerRateMin;
    this.customerRateMax = obj.customerRateMax;
    this.customerRateNoIOFMin = obj.customerRateNoIOFMin;
    this.customerRateNoIOFMax = obj.customerRateNoIOFMax;
    this.customerOperationalDocuments = customerOperationalDocuments?.length
      ? customerOperationalDocuments
      : [];
    this.documents = documents?.length ? documents : [];
    this.status = new Workflow(obj.status);
    this.bankRate = obj.bankRate;
    this.bankQuotationId = obj.bankQuotationId ?? null;
    this.duimpDueList = obj.duimpDueList || [];
    this.duimpStatus = obj.duimpStatus || null;
    this.invoiceList = obj.invoiceList || [];
    this.originCountry = obj.originCountry || null;
    this.destinationCountry = obj.destinationCountry || null;
    this.hasBankingProduct = obj.hasBankingProduct ?? null;
    this.bankingProductDocuments = obj.bankingProductDocuments || [];
    this.paymentAccount = new OperationPaymentAccountModel(obj.paymentAccount);
    this.payInAppFiatCurrency = obj.payInAppFiatCurrency || null;
    this.payInWebFiatCurrency = obj.payInWebFiatCurrency || null;
    this.payInAppToken = obj.payInAppToken || null;
    this.payOutAppToken = obj.payOutAppToken || null;
    this.payOutAppFiatCurrency = obj.payOutAppFiatCurrency || null;
    this.appSource = obj.appSource || null;
    this.stablecoinOperationType = obj.stablecoinOperationType || null;
    this.payableAppId = obj.payableAppId || null;
    this.payableAppProviderPayableId = obj.payableAppProviderPayableId || null;
    this.payableAppType = obj.payableAppType || null;
    this.payableAppAmount = obj.payableAppAmount || null;
    this.payableAppFiatCurrency = obj.payableAppFiatCurrency || null;
    this.payableAppSenderAmount = obj.payableAppSenderAmount || null;
    this.payableAppSenderCurrency = obj.payableAppSenderCurrency || null;
    this.payableAppBeneficiaryLegalName = obj.payableAppBeneficiaryLegalName || null;
    this.payableAppWalletBalanceBefore = obj.payableAppWalletBalanceBefore || null;
    this.payableAppWalletBalanceAfter = obj.payableAppWalletBalanceAfter || null;
    this.payableAppStatus = obj.payableAppStatus || null;
    this.effectiveOperationDate = obj.effectiveOperationDate;
    this.administrative = new OperationAdministrativeModel(obj.administrative);
    this.country = obj.country;
    this.cide = obj.cide;
    this.cideValue = obj.cideValue;
    this.creditedAt = obj.creditedAt;
    this.parity = obj.parity;
    this.reducedLimit = obj.reducedLimit;
    this.manualRate = obj.manualRate;
    this.createdAt = obj.createdAt;
    this.updatedAt = obj.updatedAt;
  }

  get duimpDelayDays() {
    if (!this.invoiceList?.length) return null;

    const dates = this.invoiceList
      .map(i => i.shippingForecastDate)
      .filter(Boolean);

    if (!dates.length) return null;

    const latestStr = dates.sort().pop();
    const [y, m, d] = latestStr.substring(0, 10).split('-').map(Number);
    const initialDays = this.bank?.chargeEmailInitialDays ?? 60;
    const deadline = new Date(y, m - 1, d + initialDays);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today - deadline) / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : null;
  }

  get duimpCoveredAmount() {
    return this.duimpDueList.reduce((sum, d) => sum + (parseFloat(d.value) || 0), 0);
  }

  get duimpPendingAmount() {
    return Math.max(0, (this.amount || 0) - this.duimpCoveredAmount);
  }

  get duimpCoveredPercent() {
    return !this.amount || this.amount <= 0
      ? 0
      : Math.min(100, (this.duimpCoveredAmount / this.amount) * 100);
  }

  get duimpIsOver() {
    return this.duimpCoveredAmount > (this.amount || 0);
  }

  get duimpProgressColor() {
    if (this.duimpIsOver) return 'warning';

    return this.duimpCoveredPercent >= 100 ? 'success' : 'primary';
  }

  get duimpStatusLabel() {
    if (this.duimpStatus) return DUIMP_STATUS_LABELS[this.duimpStatus] || this.duimpStatus;

    return this.duimpCoveredPercent >= 100 && !this.duimpIsOver ? DUIMP_STATUS_LABELS[DUIMP_STATUS.FINALIZADO] : DUIMP_STATUS_LABELS[DUIMP_STATUS.EM_ABERTO];
  }

  get duimpStatusLabelClass() {
    if (this.duimpStatus === DUIMP_STATUS.SEM_SOLUCAO) return 'error--text';

    if (this.duimpIsOver) return 'warning--text';

    return this.duimpCoveredPercent >= 100 ? 'success--text' : 'primary--text';
  }

  /**
   * Verifica se algum campo relevante da operação contém o termo de busca normalizado.
   * @param {string} normalizedSearch termo de busca já normalizado (sem acentos, minúsculo)
   * @returns {boolean}
   */
  matchesSearch(normalizedSearch) {
    // Helper para normalizar campos
    const normalize = (val) =>
      val
        ?.toString()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase() || '';

    // Datas formatadas
    const formattedEffectiveDate = this.effectiveOperationDate
      ? dateUtils.maskDateAndHourIso(this.effectiveOperationDate)
      : '';
    const formattedCreatedAt = this.createdAt ? dateUtils.maskDateAndHourIso(this.createdAt) : '';
    const formattedUpdatedAt = this.updatedAt ? dateUtils.maskDateAndHourIso(this.updatedAt) : '';

    // Moedas formatadas
    const formattedAmount =
      this.amount && this.currency?.code
        ? `${this.currency.code} - ${numberUtils.formatCurrency(
            this.currency.code,
            this.amount,
            this.currency.precision
          )}`
        : '';
    const formattedCustomerRate =
      this.customerRate && this.currency?.precision !== undefined
        ? numberUtils.formatCurrency('BRL', this.customerRate, this.currency.precision)
        : '';
    const formattedTotalValue = this.totalValue
      ? numberUtils.formatCurrency('BRL', this.totalValue)
      : '';
    const formattedIof = this.iofValue
      ? `${numberUtils.formatCurrency('BRL', this.iofValue || 0)} (${numberUtils.formatNumber(
          this.iof || 0
        )}%)`
      : '';
    const formattedIr = this.irValue
      ? `${numberUtils.formatCurrency('BRL', this.irValue || 0)} (${numberUtils.formatNumber(
          this.ir || 0
        )}%)`
      : '';

    // Campos principais (adapte conforme necessário)
    const liquidacaoStr = this.liquidationBrlDays != null ? `BRL D+${this.liquidationBrlDays} / ME D+${this.liquidationFxDays}` : null;

    const fields = [
      this.id,
      this.exchangeBuy ? 'Compra' : 'Venda',
      this.amount,
      this.customerRate,
      this.totalValue,
      this.bankRate,
      this.iofValue,
      this.irValue,
      this.exchangeContractCostBrl,
      this.customer?.cpfCnpj,
      this.customer?.name,
      this.customer?.companyName,
      this.customer?.email,
      this.customer?.phone,
      this.bank?.fantasyName,
      this.bank?.companyName,
      this.currency?.code,
      this.currency?.name,
      this.nature?.name,
      this.nature?.code,
      this.nature?.exibitionName,
      this.liquidation?.description,
      liquidacaoStr,
      this.status?.description,
      this.administrative?.agentData?.name,
      this.administrative?.indicatorData?.name,
      this.account?.bank,
      this.account?.name,
      this.account?.agency,
      this.account?.account,
      this.account?.swiftCodeBIC,
      this.account?.numberIBAN,
      this.account?.ownerName,
      this.city,
      this.state,
      this.neighborhood,
      this.street,
      this.number,
      this.complement,
      this.cep,
      this.receiverName,
      this.receiverDocument,
      this.receiverCellphone,
      this.observation,
      this.account?.tabNumberRouting,
      this.account?.forFurtherCredit,
      formattedEffectiveDate,
      formattedCreatedAt,
      formattedUpdatedAt,
      formattedAmount,
      formattedCustomerRate,
      formattedTotalValue,
      formattedIof,
      formattedIr,
      this.administrative?.pisCofins,
      this.administrative?.pisCofins !== undefined && this.administrative?.pisCofins !== null
        ? numberUtils.formatCurrency('BRL', this.administrative.pisCofins)
        : ''
    ];

    return fields.some((field) => {
      if (!field) return false;

      return normalize(field).includes(normalizedSearch);
    });
  }

  get liquidationDescription() {
    const brl = this.liquidationBrlDays;
    const fx = this.liquidationFxDays;

    if (brl === null || brl === undefined || fx === null || fx === undefined) return '—';

    return `D+${brl} / D+${fx}`;
  }
}
